/**
 * @fileoverview Shared Zod schemas and TypeScript types for Clockwork API.
 * These schemas are used by both frontend and backend for type safety
 * and runtime validation of data structures.
 * @module shared/schema
 */

import { z } from "zod";

/**
 * User account schema.
 * Represents a registered user with Google OAuth profile data.
 */
export const userSchema = z.object({
  /** Google user ID (sub claim from ID token) */
  id: z.string(),
  /** User's first name from Google profile */
  firstName: z.string(),
  /** User's last name from Google profile */
  lastName: z.string(),
  /** User's email address */
  email: z.string().email(),
  /** URL to user's Google profile picture */
  profileImageUrl: z.string().nullable(),
  /** Account creation timestamp */
  createdAt: z.date(),
  /** Last profile update timestamp */
  updatedAt: z.date(),
  /** Most recent login timestamp */
  lastLogin: z.date().optional(),
});

/** User type inferred from userSchema */
export type User = z.infer<typeof userSchema>;

/**
 * Monitor configuration schema.
 * Represents a complete monitor with all fields including auto-generated ones.
 */
export const monitorSchema = z.object({
  /** Unique monitor identifier (UUID) */
  id: z.string(),
  /** Owner's user ID */
  userId: z.string(),
  /** Human-readable monitor name */
  name: z.string(),
  /** Target endpoint URL to monitor */
  url: z.string().url(),
  /** HTTP method for health checks */
  method: z.enum(["GET", "POST", "PUT", "PATCH", "DELETE"]),
  /** Check interval in milliseconds */
  interval: z.number(),
  /** Whether monitor is actively running checks */
  isActive: z.boolean(),
  /** HTTP status codes that should trigger alerts */
  alertStatusCodes: z.array(z.number()),
  /** Request body for POST/PUT/PATCH methods */
  body: z.string(),
  /** Custom HTTP headers as key-value pairs */
  headers: z.record(z.string()),
  /** Monitor creation timestamp */
  createdAt: z.date(),
  /** Last configuration update timestamp */
  updatedAt: z.date(),
});

/** Monitor type inferred from monitorSchema */
export type Monitor = z.infer<typeof monitorSchema>;

/**
 * Insert monitor schema for creating new monitors.
 * Excludes auto-generated fields (id, userId, timestamps).
 * Includes validation rules and default values.
 */
export const insertMonitorSchema = z.object({
  /** Monitor name (required, min 1 character) */
  name: z.string().min(1, "Monitor name is required"),
  /** Target URL (must be valid URL format) */
  url: z.string().url("Must be a valid URL"),
  /** HTTP method (defaults to GET) */
  method: z.enum(["GET", "POST", "PUT", "PATCH", "DELETE"]).default("GET"),
  /** Check interval in ms (min 1000, defaults to 5 minutes) */
  interval: z.number().min(1000, "Interval must be at least 1000ms").default(300000),
  /** Active status (defaults to true) */
  isActive: z.boolean().default(true),
  /** Alert status codes (defaults to empty array) */
  alertStatusCodes: z.array(z.number()).default([]),
  /** Request body (defaults to empty string) */
  body: z.string().default(""),
  /** Headers as JSON string or object (defaults to empty object) */
  headers: z.union([z.string(), z.record(z.string())]).default("{}"),
});

/** InsertMonitor type for creating new monitors */
export type InsertMonitor = z.infer<typeof insertMonitorSchema>;

/**
 * Check result schema.
 * Represents a single health check execution result.
 */
export const checkResultSchema = z.object({
  /** Unique check identifier (UUID) */
  id: z.string(),
  /** Parent monitor ID */
  monitorId: z.string(),
  /** HTTP status code received (0 if request failed) */
  statusCode: z.number(),
  /** Whether check was successful (2xx-3xx response) */
  success: z.boolean(),
  /** Response time in milliseconds */
  responseTime: z.number(),
  /** Whether this check triggered an alert condition */
  isAlert: z.boolean(),
  /** Error message if check failed, null otherwise */
  errorMessage: z.string().nullable(),
  /** Timestamp when check was executed */
  checked: z.date(),
});

/** CheckResult type inferred from checkResultSchema */
export type CheckResult = z.infer<typeof checkResultSchema>;

/**
 * Subscription status enum.
 * Matches Stripe subscription statuses.
 */
export const subscriptionStatusSchema = z.enum([
  "active",
  "canceled",
  "incomplete",
  "incomplete_expired",
  "past_due",
  "paused",
  "trialing",
  "unpaid",
]);

/**
 * Subscription schema.
 * Represents a user's subscription details.
 */
export const subscriptionSchema = z.object({
  /** Unique subscription identifier (UUID or Stripe ID) */
  id: z.string(),
  /** User ID who owns the subscription */
  userId: z.string(),
  /** Stripe Customer ID */
  stripeCustomerId: z.string(),
  /** Stripe Subscription ID */
  stripeSubscriptionId: z.string(),
  /** Current status of the subscription */
  status: subscriptionStatusSchema,
  /** ID of the price/plan subscribed to */
  planId: z.string(),
  /** Timestamp when the current period ends */
  currentPeriodEnd: z.date(),
  /** Timestamp when subscription was created */
  createdAt: z.date(),
  /** Timestamp when subscription was last updated */
  updatedAt: z.date(),
});

/** Subscription type inferred from subscriptionSchema */
export type Subscription = z.infer<typeof subscriptionSchema>;
